import React from "react";
import * as Popover from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";

export const DropdownMenu = Popover.Root;
export const DropdownMenuTrigger = Popover.Trigger;

// Portal target override. Radix portals to document.body by default, which is
// outside any theme-scoped wrapper — the embed provides a host div carrying
// its resolved brand vars so portaled menus pick up the partner theme instead
// of the page defaults. Null (the default) keeps document.body.
export const DropdownMenuPortalContext = React.createContext<HTMLElement | null>(null);

interface DropdownMenuContentProps extends Popover.PopoverContentProps {
  className?: string;
}

// Styled with Tailwind theme tokens (bg-popover / border-border), not the
// legacy --bg-card / --border-default vars: this primitive is also bundled
// into the embed (HubPreview → WidgetInstallationsManagerModal), where the
// legacy vars don't exist and the portaled menu rendered transparent.
export const DropdownMenuContent = React.forwardRef<HTMLDivElement, DropdownMenuContentProps>(
  ({ children, className, align = "end", sideOffset = 6, onOpenAutoFocus, onClick, ...props }, ref) => {
    const portalContainer = React.useContext(DropdownMenuPortalContext);
    return (
      <Popover.Portal container={portalContainer ?? undefined}>
        <Popover.Content
          ref={ref}
          align={align}
          sideOffset={sideOffset}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            onOpenAutoFocus?.(e);
          }}
          // The menu is portaled out of the DOM, but it is still a React CHILD
          // of whatever rendered it, and React propagates synthetic events up
          // the React tree rather than the DOM tree — so a click on a menu item
          // reached the ancestor's onClick even though the menu is nowhere near
          // it in the DOM. Where that ancestor is a clickable row, the menu
          // silently did the row's job too: on the connections list, selecting
          // "Disconnect" armed the confirm dialog AND navigated to the detail
          // page, unmounting the dialog before it could render, so no DELETE was
          // ever sent. Stopping propagation on the trigger does not cover it —
          // the trigger's own click is the only one that travels the DOM.
          //
          // This runs in the bubble phase, after the item's handler, so items
          // still work; no DropdownMenuItem in the app delegates to an ancestor.
          onClick={(e) => {
            onClick?.(e);
            e.stopPropagation();
          }}
          className={cn(
            "z-[9999] min-w-[170px] rounded-md border border-border bg-popover p-1.5 text-popover-foreground shadow-[0_8px_24px_rgb(0_0_0_/_0.18)]",
            className,
          )}
          {...props}
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    );
  },
);

DropdownMenuContent.displayName = "DropdownMenuContent";

interface DropdownMenuItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  onSelect?: () => void;
  /**
   * Render the child as the item instead of this component's <button>, so an
   * entry that must be a real element -- an <a> that has to support
   * middle-click and cmd-click -- still closes the menu when it is used.
   * Without it such an entry has to sit loose in the content, where it is the
   * one row that leaves the menu open behind it.
   */
  asChild?: boolean;
}

export function DropdownMenuItem({
  children,
  onSelect,
  onClick,
  disabled,
  className,
  asChild,
  ...props
}: DropdownMenuItemProps): React.ReactElement {
  // Popover.Close already merges its own handler onto whatever it wraps, so
  // the child keeps its href, its classes and its own onClick.
  if (asChild) return <Popover.Close asChild>{children}</Popover.Close>;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    onSelect?.();
    onClick?.(event);
  };

  return (
    <Popover.Close asChild>
      <button
        type="button"
        disabled={disabled}
        onClick={handleClick}
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[0.82rem]",
          disabled
            ? "cursor-not-allowed text-muted-foreground"
            : "cursor-pointer text-foreground hover:bg-accent/50",
          className,
        )}
        {...props}
      >
        {children}
      </button>
    </Popover.Close>
  );
}
